<template>
  <el-collapse v-model="active">
    <el-collapse-item name="clientes">
      <template #title>
        <div class="collapse-title">
          <IconUsers />
          <span>Clientes adicionales</span>
          <el-tag
            v-if="clientes.length"
            size="default"
            type="info"
          >
            {{ clientes.length }}
          </el-tag>
        </div>
      </template>

      <el-button
        type="primary"
        size="small"
        plain
        :disabled="disabled"
        @click="openModal"
      >
        <IconPlus /> Agregar cliente
      </el-button>

      <el-table
        v-if="clientes.length"
        :data="clientes"
        size="small"
        style="margin-top: 10px"
      >
        <el-table-column
          prop="Cliente.Codigo"
          label="Código"
          width="110"
        />
        <el-table-column
          prop="Cliente.Descripcion"
          label="Descripción"
        />
        <el-table-column
          width="180"
          align="right"
        >
          <template #default="{ $index }">
            <el-button
              type="danger"
              size="small"
              plain
              :disabled="disabled"
              @click="remove($index)"
            >
              <IconDelete />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-collapse-item>
  </el-collapse>

  <!-- Modal -->
  <el-dialog
    v-model="modalVisible"
    title="Seleccionar cliente"
    width="700px"
    destroy-on-close
  >
    <el-row
      :gutter="12"
      style="margin-bottom: 10px"
    >
      <el-col :span="14">
        <el-input
          v-model="query"
          placeholder="Buscar cliente (mínimo 3 caracteres)"
          clearable
          @keyup.enter="search"
        />
      </el-col>

      <el-col
        :span="6"
        style="display: flex; gap: 8px; justify-content: flex-end"
      >
        <el-button
          type="primary"
          :loading="loading"
          :disabled="queryTrim.length < 3"
          @click="search"
        >
          Buscar
        </el-button>

        <el-button
          :disabled="loading"
          @click="clearResults"
        >
          Limpiar
        </el-button>
      </el-col>
    </el-row>

    <el-table
      v-loading="loading"
      :data="results"
      height="420"
      :row-class-name="rowClassNameResults"
      @row-dblclick="select"
    >
      <el-table-column
        prop="Codigo"
        label="Código"
        width="120"
      />
      <el-table-column
        prop="Descripcion"
        label="Descripción"
      />
      <el-table-column
        width="160"
        align="right"
      >
        <template #default="{ row }">
          <el-button
            size="small"
            type="primary"
            :disabled="disabled || isSelected(row)"
            @click="select(row)"
          >
            Seleccionar
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="modalVisible = false"> <IconClose /> Cerrar </el-button>
        <el-button
          type="primary"
          @click="modalVisible = false"
        >
          Listo
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  computed,
} from 'vue';
import { useProspectStore } from '@/views/prospects/stores/prospect_store';
import { obtenerCodigoUsuario } from '@/shared/utils/get_user_code';

import IconUsers from '~icons/mdi/account-multiple-outline';
import IconPlus from '~icons/mdi/plus';
import IconDelete from '~icons/mdi/delete-outline';
import IconClose from '~icons/mdi/close';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const active = ref([]);
const modalVisible = ref(false);
const query = ref('');
const results = ref([]);
const loading = ref(false);

const prospectStore = useProspectStore();
const { searchProspectsHTTP } = prospectStore;

const clientes = computed(() => props.modelValue);
const queryTrim = computed(() => (query.value || '').trim());

const openModal = () => {
  modalVisible.value = true;
  query.value = '';
  results.value = [];
};

const clearResults = () => {
  results.value = [];
};

const search = async () => {
  if (queryTrim.value.length < 3) return;
  loading.value = true;
  try {
    results.value = (await searchProspectsHTTP(queryTrim.value, obtenerCodigoUsuario())) || [];
  } finally {
    loading.value = false;
  }
};

const selectedCodigoSet = computed(() => {
  return new Set(
    (clientes.value || [])
      .map((c) => Number(c?.CodigoCliente))
      .filter((n) => Number.isFinite(n) && n > 0),
  );
});

const isSelected = (row) => {
  const code = Number(row?.Codigo);
  return Number.isFinite(code) && selectedCodigoSet.value.has(code);
};

const rowClassNameResults = ({ row }) => {
  return isSelected(row) ? 'row-selected' : '';
};

const select = (cliente) => {
  if (!cliente?.Codigo) return;

  const exists = clientes.value.some((c) => Number(c?.CodigoCliente) === Number(cliente.Codigo));
  if (exists) return;

  const next = [
    ...clientes.value,
    {
      CodigoCliente: cliente.Codigo,
      Cliente: { Codigo: cliente.Codigo, Descripcion: cliente.Descripcion },
      ItemCliente: clientes.value.length + 1,
    },
  ];

  emit('update:modelValue', next);
};

const remove = (index) => {
  const next = [...clientes.value];
  next.splice(index, 1);
  emit(
    'update:modelValue',
    next.map((c, i) => ({ ...c, ItemCliente: i + 1 })),
  );
};
</script>

<style scoped>
.collapse-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
:deep(.row-selected) td {
  background-color: #e9f7ef !important;
}
</style>
